import type {
  AuthenticatedOptions,
  ListOutgoingTransfersData,
  ListOutgoingTransfersResponseDtoOutput,
} from '@peanuds/shared';

import {
  mapOutgoingTransfers,
  type AnalyticsTransactionRow,
} from './analytics-transactions.boundary';
import { dedupeAndSortTransactions } from './analytics-transactions.merge';
import {
  PAYMENTS_APPROVAL_FETCH_OPTIONS,
  type PaymentsApprovalFetchOptions,
} from './payments-approval.request-options';
import { normalizeAccountIds } from './transactions.account-ids';
import { createOutgoingTransfersOptions } from './transactions.request-options';

type OutgoingTransfersResponse = Pick<ListOutgoingTransfersResponseDtoOutput, 'data'>;

type PaymentsApprovalOutgoingTransfersFetcher = (
  options?: AuthenticatedOptions<ListOutgoingTransfersData>,
) => Promise<OutgoingTransfersResponse | undefined>;

interface FetchPaymentsApprovalOutgoingTransfersParams {
  accountIds: string[];
  fetchOutgoingTransfers: PaymentsApprovalOutgoingTransfersFetcher;
  options?: PaymentsApprovalFetchOptions;
}

export const fetchPaymentsApprovalOutgoingTransfersForAccounts = async ({
  accountIds,
  fetchOutgoingTransfers,
  options,
}: FetchPaymentsApprovalOutgoingTransfersParams) => {
  const normalizedAccountIds = normalizeAccountIds(accountIds);

  if (normalizedAccountIds.length === 0) {
    return {
      normalizedAccountIds,
      responses: [],
      rows: [] as AnalyticsTransactionRow[],
      hasPartialFailure: false,
      hasLoaded: false,
    };
  }

  const resolvedOptions = {
    ...PAYMENTS_APPROVAL_FETCH_OPTIONS,
    ...options,
  };

  const responses = await Promise.all(
    normalizedAccountIds.map((accountId) => {
      return fetchOutgoingTransfers(
        createOutgoingTransfersOptions({
          accountId,
          transferDateFrom: resolvedOptions.transferDateFrom,
          transferDateTo: resolvedOptions.transferDateTo,
          pageNumber: resolvedOptions.pageNumber,
          pageSize: resolvedOptions.pageSize,
          limit: resolvedOptions.limit,
          offset: resolvedOptions.offset,
          outgoingQuery: resolvedOptions.outgoingQuery,
        }),
      );
    }),
  );

  return {
    normalizedAccountIds,
    responses,
    rows: dedupeAndSortTransactions(
      mapOutgoingTransfers(responses.flatMap((response) => response?.data ?? [])),
    ),
    hasPartialFailure: responses.some((response) => response === undefined),
    hasLoaded: responses.some((response) => response !== undefined),
  };
};
